import { Navigate, Outlet } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { SEO } from "@/components/SEO";
import { Loader2 } from "lucide-react";

interface ProtectedRouteProps {
  children?: React.ReactNode;
}

/**
 * ProtectedRoute Component
 * 
 * SECURITY NOTE: This component provides UI/UX-level route protection only.
 * It redirects unauthenticated users to the landing page for better user experience.
 * 
 * IMPORTANT: Actual data security is enforced server-side through:
 * - Express middleware authentication checks
 * - Session-based authentication via credentials auth
 * - Backend authorization on all API routes
 * 
 * Client-side route protection can be bypassed by modifying browser code,
 * but attackers cannot access any actual data due to server-side security.
 * Do not store or display sensitive information before authentication is confirmed.
 * 
 * This component supports two usage patterns:
 * 1. Layout route: <Route element={<ProtectedRoute />}> with nested child routes
 * 2. Wrapper: <Route element={<ProtectedRoute><Component /></ProtectedRoute>} />
 */
export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center py-20" data-testid="auth-loading">
        <SEO title="Loading" noIndex={true} />
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  return children ? <>{children}</> : <Outlet />;
};
